import { DragEvent, FormEvent, lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Activity, ArrowUpDown, BellRing, Bolt, Bot, CircleDollarSign, Clock3, Eye, EyeOff, FileClock, Gauge, GripVertical, LayoutDashboard, LayoutGrid, List, Menu, PanelTop, Power, RefreshCcw, Route, Search, SlidersHorizontal, WalletCards, X } from 'lucide-react';
import { Channel, ChannelBalanceAccount, ChannelSortMode, CostManagementOverview, DashboardData, DashboardGroupTimelines, DispatchStrategy, GroupSummary, OAuthPreferenceMode, Provider, RuntimeConfig, fetchChannelBalanceOverview, fetchCostManagementOverview, fetchDashboard, fetchDashboardGroupTimelines, filterChannels, saveAdminToken, saveChannelConfig, saveGroupConfig, saveRuntimeConfig, setChannelScheduling, sortChannels } from './api';
import { ChannelCard, ChannelCardBalance, ChannelConfigDraft } from './components/ChannelCard';
import { DetailDrawer } from './components/DetailDrawer';
import { ToastNotice, ToastTone } from './components/ToastNotice';
import { PRIVACY_MASK, readStoredPrivacyMode, writeStoredPrivacyMode } from './privacy';

const providerOptions: Array<{ value: Provider | 'all'; label: string }> = [
  { value: 'all', label: '全部平台' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'gemini', label: 'Gemini' },
  { value: 'openrouter', label: 'OpenRouter' },
  { value: 'grok', label: 'Grok' },
];

const DASHBOARD_REFRESH_SECONDS = 60;
const TIMELINE_REFRESH_SECONDS = 300;
const TIMELINE_REQUEST_TIMEOUT_MS = 15000;
const DEFAULT_MAX_ACCEPTABLE_TTFT_MS = 2500;
const DEFAULT_DISPATCH_STRATEGY: DispatchStrategy = 'stable_first';
const GROUP_ORDER_STORAGE_KEY = 'subpilot_group_order';
const ACTIVE_GROUP_STORAGE_KEY = 'subpilot_active_group';
const CHANNEL_VIEW_STORAGE_KEY = 'subpilot_channel_view_mode';
const CHANNEL_SORT_STORAGE_KEY = 'subpilot_channel_sort_mode';
const ACTIVE_MODULE_STORAGE_KEY = 'subpilot_active_module';
type AdminModule = 'dispatch' | 'dispatchRecords' | 'balances' | 'costs' | 'profit' | 'notifications' | 'system';
export type ChannelViewMode = 'large' | 'medium' | 'compact';

const adminModules: Array<{ id: AdminModule; label: string; mobileLabel: string; icon: typeof LayoutDashboard }> = [
  { id: 'dispatch', label: '智能调度', mobileLabel: '调度', icon: LayoutDashboard },
  { id: 'dispatchRecords', label: '调度记录', mobileLabel: '记录', icon: FileClock },
  { id: 'balances', label: '渠道余额管理', mobileLabel: '余额', icon: WalletCards },
  { id: 'costs', label: '成本管理', mobileLabel: '成本', icon: CircleDollarSign },
  { id: 'profit', label: '每日收益统计', mobileLabel: '收益', icon: Gauge },
  { id: 'notifications', label: '消息推送', mobileLabel: '推送', icon: BellRing },
  { id: 'system', label: '系统设置', mobileLabel: '系统', icon: SlidersHorizontal },
];

const channelViewOptions: Array<{ value: ChannelViewMode; label: string; icon: typeof LayoutGrid }> = [
  { value: 'large', label: '大', icon: LayoutGrid },
  { value: 'medium', label: '中', icon: PanelTop },
  { value: 'compact', label: '小', icon: List },
];

const loadDispatchRecordsPage = () => import('./components/DispatchRecordsPage').then((module) => ({ default: module.DispatchRecordsPage }));
const loadChannelBalancePage = () => import('./components/ChannelBalancePage').then((module) => ({ default: module.ChannelBalancePage }));
const loadCostManagementPage = () => import('./components/CostManagementPage').then((module) => ({ default: module.CostManagementPage }));
const loadDailyProfitPage = () => import('./components/DailyProfitPage').then((module) => ({ default: module.DailyProfitPage }));
const loadNotificationSettingsPage = () => import('./components/NotificationSettingsPage').then((module) => ({ default: module.NotificationSettingsPage }));
const loadSystemSettingsPage = () => import('./components/SystemSettingsPage').then((module) => ({ default: module.SystemSettingsPage }));

const DispatchRecordsPage = lazy(loadDispatchRecordsPage);
const ChannelBalancePage = lazy(loadChannelBalancePage);
const CostManagementPage = lazy(loadCostManagementPage);
const DailyProfitPage = lazy(loadDailyProfitPage);
const NotificationSettingsPage = lazy(loadNotificationSettingsPage);
const SystemSettingsPage = lazy(loadSystemSettingsPage);

function App() {
  const isMobileViewport = useMobileViewport();
  const [activeModule, setActiveModule] = useState<AdminModule>(readStoredActiveModule);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [groupId, setGroupId] = useState(readStoredActiveGroup);
  const [provider, setProvider] = useState<Provider | 'all'>('all');
  const [search, setSearch] = useState('');
  const [smartMode, setSmartMode] = useState(true);
  const [probeIntervalSeconds, setProbeIntervalSeconds] = useState(60);
  const [probeImageIntervalSeconds, setProbeImageIntervalSeconds] = useState(1200);
  const [probeJitterSeconds, setProbeJitterSeconds] = useState(0);
  const [dispatchFailOpen, setDispatchFailOpen] = useState(true);
  const [dispatchSelectTimeoutMs, setDispatchSelectTimeoutMs] = useState(80);
  const [dispatchAutoBypassFailures, setDispatchAutoBypassFailures] = useState(3);
  const [dispatchAutoRecover, setDispatchAutoRecover] = useState(true);
  const [probeMaxConcurrentAccounts, setProbeMaxConcurrentAccounts] = useState(8);
  const [probeTimeoutSeconds, setProbeTimeoutSeconds] = useState(30);
  const [probeRetryCount, setProbeRetryCount] = useState(1);
  const [alertMergeWindowMinutes, setAlertMergeWindowMinutes] = useState(30);
  const [dataRetentionDays, setDataRetentionDays] = useState(7);
  const [maxAcceptableTtftSec, setMaxAcceptableTtftSec] = useState(formatTTFTSeconds(DEFAULT_MAX_ACCEPTABLE_TTFT_MS));
  const [dispatchStrategy, setDispatchStrategy] = useState<DispatchStrategy>(DEFAULT_DISPATCH_STRATEGY);
  const [selected, setSelected] = useState<Channel | null>(null);
  const [saveStatus, setSaveStatus] = useState('');
  const [dashboardError, setDashboardError] = useState('');
  const [dispatchEnabled, setDispatchEnabled] = useState(false);
  const [dispatchOAuthPreference, setDispatchOAuthPreference] = useState<OAuthPreferenceMode>('prefer_healthy');
  const [probeEnabled, setProbeEnabled] = useState(false);
  const [lastLoadedAtMs, setLastLoadedAtMs] = useState<number | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [privacyMode, setPrivacyMode] = useState(readStoredPrivacyMode);
  const [useRolling24h, setUseRolling24h] = useState(false);
  const [authRequired, setAuthRequired] = useState(false);
  const [adminTokenDraft, setAdminTokenDraft] = useState('');
  const [channelViewMode, setChannelViewMode] = useState<ChannelViewMode>(readStoredChannelViewMode);
  const [channelSortMode, setChannelSortMode] = useState<ChannelSortMode>(readStoredChannelSortMode);
  const [groupOrder, setGroupOrder] = useState<string[]>(readStoredGroupOrder);
  const [draggingGroupId, setDraggingGroupId] = useState<string | null>(null);
  const [dispatchingOnly, setDispatchingOnly] = useState(false);
  const [channelBalances, setChannelBalances] = useState<Record<string, ChannelCardBalance>>({});
  const [timelineRefreshVersion, setTimelineRefreshVersion] = useState(0);
  const dashboardRequestSequence = useRef(0);
  const balanceAccountsRef = useRef<ChannelBalanceAccount[] | null>(null);
  const costOverviewRef = useRef<CostManagementOverview | null>(null);
  const balanceAccountsSequence = useRef(0);
  const costOverviewSequence = useRef(0);
  const auxiliaryRequestSequence = useRef(0);
  const dashboardInitialized = useRef(false);

  useEffect(() => {
    const controller = new AbortController();
    void loadDashboard(controller.signal);
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      setActiveModule(readActiveModuleFromLocation() ?? readStoredActiveModule());
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => void loadDashboard(undefined, false), DASHBOARD_REFRESH_SECONDS * 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (activeModule !== 'dispatch') return;
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        setTimelineRefreshVersion((version) => version + 1);
      }
    }, TIMELINE_REFRESH_SECONDS * 1000);
    return () => window.clearInterval(timer);
  }, [activeModule]);

  useEffect(() => {
    if (!dashboard || activeModule !== 'dispatch') return;
    const targetGroupIds = dispatchingOnly
      ? Array.from(new Set(dashboard.channels.filter((channel) => channel.isDispatching).map((channel) => channel.group.id)))
      : [groupId];
    if (targetGroupIds.length === 0 || targetGroupIds.some((targetGroupId) => !targetGroupId)) return;

    let disposed = false;
    const controllers = new Map<string, AbortController>();
    setDashboard((current) => markTimelineGroupsLoading(current, targetGroupIds));

    const loadGroupTimeline = async (targetGroupId: string): Promise<void> => {
      const controller = new AbortController();
      controllers.set(targetGroupId, controller);
      const timeout = window.setTimeout(() => controller.abort(), TIMELINE_REQUEST_TIMEOUT_MS);
      try {
        const response = await fetchDashboardGroupTimelines(targetGroupId, { signal: controller.signal });
        if (disposed) return;
        // Commit each group as soon as it arrives. One slow group must not
        // keep already loaded cards in a perpetual loading state.
        setDashboard((current) => mergeTimelineResponses(current, [response]));
        setSelected((current) => mergeSelectedTimelineResponses(current, [response]));
      } catch {
        if (disposed) return;
        setDashboard((current) => markTimelineGroupsFailed(current, [targetGroupId]));
        setSelected((current) => mergeSelectedTimelineResponses(current, [{ groupId: targetGroupId, channels: [], updatedAt: new Date().toISOString() }]));
      } finally {
        window.clearTimeout(timeout);
        controllers.delete(targetGroupId);
      }
    };

    targetGroupIds.forEach((targetGroupId) => { void loadGroupTimeline(targetGroupId); });
    return () => {
      disposed = true;
      controllers.forEach((controller) => controller.abort());
      controllers.clear();
    };
  }, [activeModule, timelineRefreshVersion, dispatchingOnly, groupId]);

  useEffect(() => {
    syncActiveModuleLocation(activeModule);
  }, [activeModule]);

  async function loadChannelBalances(signal?: AbortSignal): Promise<void> {
    const requestSequence = ++auxiliaryRequestSequence.current;
    const [balanceResult, costResult] = await Promise.allSettled([
      fetchChannelBalanceOverview({ signal }),
      fetchCostManagementOverview({ signal }),
    ]);
    if (signal?.aborted) return;

    if (balanceResult.status === 'fulfilled' && requestSequence >= balanceAccountsSequence.current) {
      balanceAccountsSequence.current = requestSequence;
      balanceAccountsRef.current = balanceResult.value.items ?? [];
    }
    if (costResult.status === 'fulfilled' && requestSequence >= costOverviewSequence.current) {
      costOverviewSequence.current = requestSequence;
      costOverviewRef.current = costResult.value;
    }
    if (balanceAccountsRef.current && costOverviewRef.current) {
      setChannelBalances(buildChannelBalanceMap(balanceAccountsRef.current, costOverviewRef.current));
    }
  }

  async function loadDashboard(signal?: AbortSignal, includeAuxiliary = true): Promise<boolean> {
    const requestSequence = ++dashboardRequestSequence.current;
    try {
      // Start auxiliary card data in parallel, but never make the dispatch
      // dashboard wait for either upstream-management endpoint.
      if (includeAuxiliary) void loadChannelBalances(signal);
      const data = await fetchDashboard({ signal });
      if (signal?.aborted || requestSequence !== dashboardRequestSequence.current) return false;
      setDashboard((current) => preserveDashboardTimelines(data, current));
      setDashboardError('');
      setAuthRequired(false);
      setDispatchEnabled(data.runtime?.dispatchEnabled ?? false);
      setDispatchOAuthPreference(data.runtime?.dispatchOAuthPreference ?? 'prefer_healthy');
      setProbeEnabled(data.runtime?.probeEnabled ?? false);
      setProbeIntervalSeconds(data.runtime?.probeIntervalSeconds ?? 60);
      setProbeImageIntervalSeconds(normalizeProbeImageIntervalSeconds(data.runtime?.probeImageIntervalSeconds ?? 1200));
      setProbeJitterSeconds(normalizeProbeJitterSeconds(data.runtime?.probeJitterSeconds ?? 0));
      setDispatchFailOpen(data.runtime?.dispatchFailOpen ?? true);
      setDispatchSelectTimeoutMs(data.runtime?.dispatchSelectTimeoutMs ?? 80);
      setDispatchAutoBypassFailures(data.runtime?.dispatchAutoBypassFailures ?? 3);
      setDispatchAutoRecover(data.runtime?.dispatchAutoRecover ?? true);
      setProbeMaxConcurrentAccounts(data.runtime?.probeMaxConcurrentAccounts ?? 8);
      setProbeTimeoutSeconds(data.runtime?.probeTimeoutSeconds ?? 30);
      setProbeRetryCount(data.runtime?.probeRetryCount ?? 1);
      setAlertMergeWindowMinutes(data.runtime?.alertMergeWindowMinutes ?? 30);
      setDataRetentionDays(normalizeDataRetentionDays(data.runtime?.dataRetentionDays ?? 7));
      setLastLoadedAtMs(Date.now());
      setNowMs(Date.now());
      if (!dashboardInitialized.current) {
        dashboardInitialized.current = true;
        setTimelineRefreshVersion((version) => version + 1);
      }
      setSelected((current) => current ? data.channels.find((channel) => channel.id === current.id) ?? null : null);
      setGroupId((currentGroupId) => {
        const nextGroup = data.groups.find((group) => group.id === currentGroupId) ?? data.groups[0];
        if (nextGroup) writeStoredActiveGroup(nextGroup.id);
        setSmartMode(nextGroup?.mode !== 'forced');
        setMaxAcceptableTtftSec(formatTTFTSeconds(nextGroup?.maxAcceptableTtftMs ?? DEFAULT_MAX_ACCEPTABLE_TTFT_MS));
        setDispatchStrategy(groupDispatchStrategy(nextGroup));
        return nextGroup?.id ?? currentGroupId;
      });
      return true;
    } catch (error) {
      if (signal?.aborted || requestSequence !== dashboardRequestSequence.current) return false;
      const message = error instanceof Error ? error.message : '';
      if (message === 'unauthorized' || message.includes('401')) {
        setAuthRequired(true);
        setDashboardError('后台令牌无效或未配置');
      } else {
        setDashboardError(error instanceof Error ? `Dashboard API 不可用：${error.message}` : 'Dashboard API 不可用');
      }
      return false;
    }
  }

  async function handleAdminLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    saveAdminToken(adminTokenDraft);
    setAuthRequired(false);
    setDashboardError('');
    setSaveStatus('后台令牌已保存，正在重新加载...');
    await loadDashboard();
  }

  async function handleRefresh() {
    setIsRefreshing(true);
    setSaveStatus('正在刷新监控数据...');
    try {
      const refreshed = await loadDashboard();
      if (refreshed) setTimelineRefreshVersion((version) => version + 1);
      setSaveStatus(refreshed ? '监控数据已刷新' : '监控数据刷新失败，已保留旧数据');
    } finally {
      setIsRefreshing(false);
    }
  }

  function handlePrivacyModeChange(nextPrivacyMode: boolean) {
    setPrivacyMode(nextPrivacyMode);
    writeStoredPrivacyMode(nextPrivacyMode);
  }

  function handleChannelSortModeChange(nextSortMode: ChannelSortMode) {
    setChannelSortMode(nextSortMode);
    writeStoredChannelSortMode(nextSortMode);
  }

  const channels = useMemo(() => {
    if (!dashboard) return [];
    const filtered = filterChannels(dashboard.channels, { groupId: dispatchingOnly ? 'all' : groupId, provider, search });
    const visible = dispatchingOnly ? filtered.filter((channel) => channel.isDispatching) : filtered;
    return sortChannels(visible, channelSortMode);
  }, [channelSortMode, dashboard, dispatchingOnly, groupId, provider, search]);
  const orderedGroups = useMemo(() => orderGroups(dashboard?.groups ?? [], groupOrder), [dashboard?.groups, groupOrder]);
  const groupedGroups = useMemo(
    () => groupGroupsByProvider(orderedGroups, dashboard?.channels ?? []),
    [orderedGroups, dashboard?.channels],
  );

  const activeGroup = dashboard?.groups.find((group) => group.id === groupId);
  const totalRequests = channels.reduce((sum, channel) => sum + (useRolling24h ? channel.request24h : (channel.requestToday ?? channel.request24h)), 0);
  const healthyCount = channels.filter((channel) => (channel.dispatchHealthGrade ?? channel.status) === 'healthy').length;
  const averageAvailability = channels.length
    ? channels.reduce((sum, channel) => sum + (useRolling24h ? channel.availability7d : (channel.availabilityToday ?? channel.availability7d)), 0) / channels.length
    : 0;
  const statisticsWindowLabel = useRolling24h ? '近24小时' : '今日';
  const compositeCost = useRolling24h ? activeGroup?.compositeCost24h : (activeGroup?.compositeCostToday ?? activeGroup?.compositeCost24h);
  const refreshLabel = refreshCountdownLabel(lastLoadedAtMs, nowMs);
  const effectiveChannelViewMode: ChannelViewMode = isMobileViewport ? 'compact' : channelViewMode;
  const channelRefreshLabel = effectiveChannelViewMode === 'large' ? refreshLabel : '';
  const appToastMessage = dashboardError || saveStatus;
  const appToastTone: ToastTone = dashboardError ? 'error' : statusTone(saveStatus);

  async function handleSaveChannel(channel: Channel, config: ChannelConfigDraft) {
    await saveChannelConfig(channel.id, {
      enabled: true,
      manualPriority: config.manualPriority,
      maxConcurrency: 0,
      costCnyPerOfficialUsd: config.costCnyPerUsd,
      probeIntervalSeconds: 0,
      probeModel: config.probeModel,
      probePrompt: config.probePrompt,
      probeAsClaudeCode: config.probeAsClaudeCode,
    });
    setDashboard((current) => current && {
      ...current,
      channels: current.channels.map((item) => item.id === channel.id
        ? {
            ...item,
            manualPriority: config.manualPriority,
            costCnyPerUsd: config.costCnyPerUsd,
            probeModel: config.probeModel,
            probePrompt: config.probePrompt,
            probeAsClaudeCode: config.probeAsClaudeCode,
          }
        : item),
    });
    setSaveStatus(`${channel.name} 已保存`);
  }

  async function handleChannelSchedulingChange(channel: Channel, enabled: boolean) {
    try {
      await setChannelScheduling(channel.id, enabled);
      setDashboard((current) => current && {
        ...current,
        channels: current.channels.map((item) => item.id === channel.id
          ? { ...item, enabled, schedulable: enabled, timelinesLoading: enabled }
          : item),
      });
      setSelected((current) => current?.id === channel.id ? { ...current, enabled, schedulable: enabled } : current);
      if (enabled) setTimelineRefreshVersion((version) => version + 1);
      setSaveStatus(`${channel.name} 已${enabled ? '开启' : '停用'}调度`);
    } catch (error) {
      setDashboardError(error instanceof Error ? `调度开关保存失败：${error.message}` : '调度开关保存失败');
      throw error;
    }
  }

  async function handleForceChannel(channel: Channel) {
    try {
      await saveGroupConfig(channel.group.id, {
        enabled: true,
        forcedAccountId: channel.id,
        forcedFallback: 'reject',
        stickyTtlMs: 86_400_000,
        maxAcceptableTtftMs: groupMaxAcceptableTTFTMS(channel.group),
        probeIntervalSeconds: channel.group.probeIntervalSec,
        dispatchStrategy: groupDispatchStrategy(channel.group),
        includeNonStreamUsage: groupIncludeNonStreamUsage(channel.group),
      }, true);
      setSmartMode(false);
      setDashboard((current) => current && {
        ...current,
        groups: current.groups.map((group) => group.id === channel.group.id
          ? { ...group, mode: 'forced', forcedChannelId: channel.id }
          : group),
        channels: current.channels.map((item) => item.group.id === channel.group.id
          ? { ...item, forced: item.id === channel.id, group: { ...item.group, mode: 'forced', forcedChannelId: channel.id } }
          : item),
      });
      setSaveStatus(`${channel.group.name} 已强制到 ${channel.name}`);
    } catch (error) {
      setDashboardError(error instanceof Error ? `强制账号保存失败：${error.message}` : '强制账号保存失败');
    }
  }

  async function handleSmartModeChange(nextSmartMode: boolean) {
    setSmartMode(nextSmartMode);
    if (!activeGroup) return;
    await saveGroupConfig(activeGroup.id, {
      enabled: true,
      forcedAccountId: nextSmartMode ? '' : activeGroup.forcedChannelId ?? '',
      forcedFallback: nextSmartMode ? 'smart' : 'reject',
      stickyTtlMs: 86_400_000,
      maxAcceptableTtftMs: groupMaxAcceptableTTFTMS(activeGroup),
      probeIntervalSeconds: activeGroup.probeIntervalSec,
      dispatchStrategy: groupDispatchStrategy(activeGroup),
      includeNonStreamUsage: groupIncludeNonStreamUsage(activeGroup),
    });
    setDashboard((current) => current && {
      ...current,
      groups: current.groups.map((group) => group.id === activeGroup.id
        ? { ...group, mode: nextSmartMode ? 'smart' : 'forced', forcedChannelId: nextSmartMode ? undefined : group.forcedChannelId }
        : group),
      channels: current.channels.map((channel) => channel.group.id === activeGroup.id && nextSmartMode
        ? { ...channel, forced: false, group: { ...channel.group, mode: 'smart', forcedChannelId: undefined } }
        : channel),
    });
    setSaveStatus(nextSmartMode ? `${activeGroup.name} 已切回智能调度` : `${activeGroup.name} 已切到强制模式`);
  }

  async function handleMaxAcceptableTtftBlur() {
    if (!activeGroup) return;
    const nextTtftMS = ttftSecondsInputToMS(maxAcceptableTtftSec, groupMaxAcceptableTTFTMS(activeGroup));
    setMaxAcceptableTtftSec(formatTTFTSeconds(nextTtftMS));
    await saveGroupConfig(activeGroup.id, {
      enabled: true,
      forcedAccountId: activeGroup.forcedChannelId ?? '',
      forcedFallback: groupForcedFallback(activeGroup),
      stickyTtlMs: 86_400_000,
      maxAcceptableTtftMs: nextTtftMS,
      probeIntervalSeconds: activeGroup.probeIntervalSec,
      dispatchStrategy: groupDispatchStrategy(activeGroup),
      includeNonStreamUsage: groupIncludeNonStreamUsage(activeGroup),
    });
    setDashboard((current) => current && {
      ...current,
      groups: current.groups.map((group) => group.id === activeGroup.id ? { ...group, maxAcceptableTtftMs: nextTtftMS } : group),
      channels: current.channels.map((channel) => channel.group.id === activeGroup.id
        ? { ...channel, group: { ...channel.group, maxAcceptableTtftMs: nextTtftMS } }
        : channel),
    });
    setSaveStatus(`${activeGroup.name} 最长首字已保存`);
  }

  async function handleDispatchStrategyChange(nextStrategy: DispatchStrategy) {
    setDispatchStrategy(nextStrategy);
    if (!activeGroup) return;
    await saveGroupConfig(activeGroup.id, {
      enabled: true,
      forcedAccountId: activeGroup.forcedChannelId ?? '',
      forcedFallback: groupForcedFallback(activeGroup),
      stickyTtlMs: 86_400_000,
      maxAcceptableTtftMs: groupMaxAcceptableTTFTMS(activeGroup),
      probeIntervalSeconds: activeGroup.probeIntervalSec,
      dispatchStrategy: nextStrategy,
      includeNonStreamUsage: groupIncludeNonStreamUsage(activeGroup),
    });
    setDashboard((current) => current && {
      ...current,
      groups: current.groups.map((group) => group.id === activeGroup.id ? { ...group, dispatchStrategy: nextStrategy } : group),
      channels: current.channels.map((channel) => channel.group.id === activeGroup.id
        ? { ...channel, group: { ...channel.group, dispatchStrategy: nextStrategy } }
        : channel),
    });
    setSaveStatus(`${activeGroup.name} 调度策略已保存`);
  }

  async function handleIncludeNonStreamUsageChange(nextEnabled: boolean) {
    if (!activeGroup) return;
    await saveGroupConfig(activeGroup.id, {
      enabled: true,
      forcedAccountId: activeGroup.forcedChannelId ?? '',
      forcedFallback: groupForcedFallback(activeGroup),
      stickyTtlMs: 86_400_000,
      maxAcceptableTtftMs: groupMaxAcceptableTTFTMS(activeGroup),
      probeIntervalSeconds: activeGroup.probeIntervalSec,
      dispatchStrategy: groupDispatchStrategy(activeGroup),
      includeNonStreamUsage: nextEnabled,
    });
    setDashboard((current) => current && {
      ...current,
      groups: current.groups.map((group) => group.id === activeGroup.id ? { ...group, includeNonStreamUsage: nextEnabled } : group),
      channels: current.channels.map((channel) => channel.group.id === activeGroup.id
        ? { ...channel, group: { ...channel.group, includeNonStreamUsage: nextEnabled } }
        : channel),
    });
    setSaveStatus(nextEnabled ? `${activeGroup.name} 已监测非流式真实请求` : `${activeGroup.name} 已忽略非流式真实请求`);
  }

  async function handleDispatchEnabledChange(nextEnabled: boolean) {
    const previous = dispatchEnabled;
    setDispatchEnabled(nextEnabled);
    setDashboard((current) => current && {
      ...current,
      runtime: runtimeConfig({ dispatchEnabled: nextEnabled }),
    });
    try {
      await saveRuntimeConfig(runtimeConfig({ dispatchEnabled: nextEnabled }));
      setSaveStatus(nextEnabled ? 'SubPilot 已接管真实调度' : 'SubPilot 已停止接管，当前走 Sub2API 原生调度');
    } catch (error) {
      setDispatchEnabled(previous);
      setDashboard((current) => current && {
        ...current,
        runtime: runtimeConfig({ dispatchEnabled: previous }),
      });
      setSaveStatus(error instanceof Error ? `接管调度保存失败：${error.message}` : '接管调度保存失败');
    }
  }

  async function handleProbeEnabledChange(nextEnabled: boolean) {
    const previous = probeEnabled;
    setProbeEnabled(nextEnabled);
    setDashboard((current) => current && {
      ...current,
      runtime: runtimeConfig({ probeEnabled: nextEnabled }),
    });
    try {
      await saveRuntimeConfig(runtimeConfig({ probeEnabled: nextEnabled }));
      setSaveStatus(nextEnabled ? 'SubPilot 已开启自动探测' : 'SubPilot 已关闭自动探测');
    } catch (error) {
      setProbeEnabled(previous);
      setDashboard((current) => current && {
        ...current,
        runtime: runtimeConfig({ probeEnabled: previous }),
      });
      setSaveStatus(error instanceof Error ? `自动探测保存失败：${error.message}` : '自动探测保存失败');
    }
  }

  async function handleProbeIntervalSecondsBlur() {
    const nextSeconds = normalizeProbeIntervalSeconds(probeIntervalSeconds);
    setProbeIntervalSeconds(nextSeconds);
    setDashboard((current) => current && {
      ...current,
      runtime: runtimeConfig({ probeIntervalSeconds: nextSeconds }),
    });
    try {
      await saveRuntimeConfig(runtimeConfig({ probeIntervalSeconds: nextSeconds }));
      setSaveStatus(`全局探测间隔已保存为 ${nextSeconds} 秒`);
    } catch (error) {
      setSaveStatus(error instanceof Error ? `全局探测间隔保存失败：${error.message}` : '全局探测间隔保存失败');
    }
  }

  async function handleProbeImageIntervalSecondsBlur() {
    const nextSeconds = normalizeProbeImageIntervalSeconds(probeImageIntervalSeconds);
    setProbeImageIntervalSeconds(nextSeconds);
    setDashboard((current) => current && {
      ...current,
      runtime: runtimeConfig({ probeImageIntervalSeconds: nextSeconds }),
    });
    try {
      await saveRuntimeConfig(runtimeConfig({ probeImageIntervalSeconds: nextSeconds }));
      setSaveStatus(`gpt-image-2 探测间隔已保存为 ${formatIntervalMinutes(nextSeconds)}`);
    } catch (error) {
      setSaveStatus(error instanceof Error ? `gpt-image-2 探测间隔保存失败：${error.message}` : 'gpt-image-2 探测间隔保存失败');
    }
  }

  async function handleProbeJitterSecondsBlur() {
    const nextSeconds = normalizeProbeJitterSeconds(probeJitterSeconds);
    setProbeJitterSeconds(nextSeconds);
    setDashboard((current) => current && {
      ...current,
      runtime: runtimeConfig({ probeJitterSeconds: nextSeconds }),
    });
    try {
      await saveRuntimeConfig(runtimeConfig({ probeJitterSeconds: nextSeconds }));
      setSaveStatus(`探测抖动已保存为 ${nextSeconds} 秒`);
    } catch (error) {
      setSaveStatus(error instanceof Error ? `探测抖动保存失败：${error.message}` : '探测抖动保存失败');
    }
  }

  async function handleDataRetentionDaysChange(nextDays: number) {
    const normalized = normalizeDataRetentionDays(nextDays);
    const previous = dataRetentionDays;
    setDataRetentionDays(normalized);
    setDashboard((current) => current && {
      ...current,
      runtime: runtimeConfig({ dataRetentionDays: normalized }),
    });
    try {
      await saveRuntimeConfig(runtimeConfig({ dataRetentionDays: normalized }));
      setSaveStatus(`监控数据保留时间已保存为 ${normalized} 天`);
    } catch (error) {
      setDataRetentionDays(previous);
      setDashboard((current) => current && {
        ...current,
        runtime: runtimeConfig({ dataRetentionDays: previous }),
      });
      setSaveStatus(error instanceof Error ? `监控数据保留时间保存失败：${error.message}` : '监控数据保留时间保存失败');
    }
  }

  function runtimeConfig(overrides: Partial<RuntimeConfig> = {}): RuntimeConfig {
    return {
      dispatchEnabled,
      dispatchOAuthPreference,
      dispatchFailOpen,
      dispatchSelectTimeoutMs,
      dispatchAutoBypassFailures,
      dispatchAutoRecover,
      probeEnabled,
      probeIntervalSeconds,
      probeImageIntervalSeconds,
      probeJitterSeconds,
      probeMaxConcurrentAccounts,
      probeTimeoutSeconds,
      probeRetryCount,
      alertMergeWindowMinutes,
      dataRetentionDays,
      ...overrides,
    };
  }

  function applyRuntimeConfig(config: RuntimeConfig) {
    setDispatchEnabled(config.dispatchEnabled);
    setDispatchOAuthPreference(config.dispatchOAuthPreference);
    setDispatchFailOpen(config.dispatchFailOpen);
    setDispatchSelectTimeoutMs(config.dispatchSelectTimeoutMs);
    setDispatchAutoBypassFailures(config.dispatchAutoBypassFailures);
    setDispatchAutoRecover(config.dispatchAutoRecover);
    setProbeEnabled(config.probeEnabled);
    setProbeIntervalSeconds(config.probeIntervalSeconds);
    setProbeImageIntervalSeconds(config.probeImageIntervalSeconds);
    setProbeJitterSeconds(config.probeJitterSeconds);
    setProbeMaxConcurrentAccounts(config.probeMaxConcurrentAccounts);
    setProbeTimeoutSeconds(config.probeTimeoutSeconds);
    setProbeRetryCount(config.probeRetryCount);
    setAlertMergeWindowMinutes(config.alertMergeWindowMinutes);
    setDataRetentionDays(config.dataRetentionDays);
    setDashboard((current) => current && { ...current, runtime: config });
  }

  async function handleAdvancedRuntimeChange(patch: Partial<RuntimeConfig>, message: string) {
    const previous = runtimeConfig();
    const next = { ...previous, ...patch };
    applyRuntimeConfig(next);
    try {
      await saveRuntimeConfig(next);
      setSaveStatus(message);
    } catch (error) {
      applyRuntimeConfig(previous);
      setSaveStatus(error instanceof Error ? `系统设置保存失败：${error.message}` : '系统设置保存失败');
    }
  }

  function handleChannelViewModeChange(nextMode: ChannelViewMode) {
    setChannelViewMode(nextMode);
    writeStoredChannelViewMode(nextMode);
  }

  function handleGroupDragStart(event: DragEvent<HTMLButtonElement>, draggedId: string) {
    setDraggingGroupId(draggedId);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', draggedId);
  }

  function handleGroupDragOver(event: DragEvent<HTMLButtonElement>, targetId: string) {
    const draggedId = event.dataTransfer.getData('text/plain') || draggingGroupId;
    if (!draggedId || groupSectionKey(groupedGroups, draggedId) !== groupSectionKey(groupedGroups, targetId)) {
      event.dataTransfer.dropEffect = 'none';
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }

  function handleGroupDrop(event: DragEvent<HTMLButtonElement>, targetId: string) {
    event.preventDefault();
    const draggedId = event.dataTransfer.getData('text/plain') || draggingGroupId;
    if (!draggedId || draggedId === targetId) {
      setDraggingGroupId(null);
      return;
    }
    if (groupSectionKey(groupedGroups, draggedId) !== groupSectionKey(groupedGroups, targetId)) {
      setDraggingGroupId(null);
      return;
    }
    const nextOrder = moveGroupInOrder(orderedGroups.map((group) => group.id), draggedId, targetId);
    setGroupOrder(nextOrder);
    writeStoredGroupOrder(nextOrder);
    setDraggingGroupId(null);
    setSaveStatus('分组顺序已保存在当前浏览器');
  }

  function preloadModule(module: AdminModule) {
    if (module === 'dispatchRecords') void loadDispatchRecordsPage();
    if (module === 'balances') void loadChannelBalancePage();
    if (module === 'costs') void loadCostManagementPage();
    if (module === 'profit') void loadDailyProfitPage();
    if (module === 'notifications') void loadNotificationSettingsPage();
    if (module === 'system') void loadSystemSettingsPage();
  }

  function handleModuleChange(module: AdminModule) {
    preloadModule(module);
    setActiveModule(module);
    setMobileNavOpen(false);
  }

  if (authRequired) {
    return (
      <main className="auth-shell">
        <form className="auth-card" onSubmit={(event) => void handleAdminLogin(event)}>
          <span className="brand-mark"><Bot size={24} /></span>
          <div>
            <span className="eyebrow">SubPilot Admin</span>
            <h1>管理员登录</h1>
            <p>请输入服务器配置的后台令牌，保存后会保存在当前浏览器，用手机访问时也可以直接登录。</p>
          </div>
          <label className="field">
            <span>管理员令牌</span>
            <input
              aria-label="管理员令牌"
              type="password"
              value={adminTokenDraft}
              autoComplete="current-password"
              onChange={(event) => setAdminTokenDraft(event.target.value)}
            />
          </label>
          {dashboardError && <p className="save-status error-status" role="status">{dashboardError}</p>}
          <button className="primary-button" type="submit" disabled={adminTokenDraft.trim().length === 0}>保存并继续</button>
        </form>
      </main>
    );
  }

  return (
    <main className="app-shell">
      <a className="skip-link" href="#main-content">跳到主内容</a>
      {mobileNavOpen && (
        <div className="nav-backdrop" onClick={() => setMobileNavOpen(false)} aria-hidden="true" />
      )}

      <aside className={mobileNavOpen ? 'sidebar is-open' : 'sidebar'}>
        <div className="sidebar-inner">
          <div className="brand">
            <span className="brand-mark"><Bot size={24} /></span>
            <div>
              <p>智能分流后台</p>
              <strong>SubPilot</strong>
            </div>
          </div>
          <nav className="module-nav" aria-label="主导航">
            {adminModules.map((item) => {
              const Icon = item.icon;
              return (
                  <button
                  type="button"
                  className={activeModule === item.id ? 'module-nav-item active' : 'module-nav-item'}
                  aria-current={activeModule === item.id ? 'page' : undefined}
                  key={item.id}
                  data-mobile-label={item.mobileLabel}
                  onMouseEnter={() => preloadModule(item.id)}
                  onFocus={() => preloadModule(item.id)}
                  onClick={() => handleModuleChange(item.id)}
                >
                  <Icon size={18} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
        <button className="sidebar-close" onClick={() => setMobileNavOpen(false)} aria-label="关闭菜单">
          <X size={18} />
        </button>
      </aside>

      <div className="workspace" id="main-content">
        <header className="mobile-topbar">
          <button className="hamburger" type="button" onClick={() => setMobileNavOpen(true)} aria-label="打开菜单">
            <Menu size={22} />
          </button>
          <span className="mobile-brand">SubPilot</span>
        </header>
        {activeModule === 'dispatch' && (
          <section className="module-page" aria-label="智能调度">
            <section className="dispatch-dashboard-grid" aria-label="调度总览">
              <section className="dashboard-kpi-panel">
                <div className="statistics-window-bar">
                  <span><Clock3 size={15} />统计区间</span>
                  <label className={useRolling24h ? 'statistics-window-switch enabled' : 'statistics-window-switch'}>
                    <input type="checkbox" role="switch" aria-label="统计近24小时" checked={useRolling24h} onChange={(event) => setUseRolling24h(event.target.checked)} />
                    <span aria-hidden="true" />
                    <strong>{useRolling24h ? '近24小时' : '今日 00:00 起'}</strong>
                  </label>
                </div>
                <section className="kpi-grid" aria-label="全局指标">
                  <article className="metric-card metric-card-lead"><span>当前分组</span><strong>{dispatchingOnly ? '正在调度' : activeGroup?.name ?? '全部'}</strong></article>
                  <article className="metric-card"><span>健康账号</span><strong>{healthyCount}/{channels.length}</strong></article>
                  <article className="metric-card"><span>{statisticsWindowLabel}请求</span><strong>{privacyMode ? PRIVACY_MASK : totalRequests.toLocaleString()}</strong></article>
                  <article className="metric-card"><span>{statisticsWindowLabel}综合可用性</span><strong>{averageAvailability.toFixed(2)}%</strong></article>
                  <article className="metric-card"><span>{statisticsWindowLabel}综合成本</span><strong>{privacyMode ? PRIVACY_MASK : formatCompositeCost(compositeCost)}</strong></article>
                </section>
              </section>

              <aside className="runtime-panel" aria-label="运行开关">
                <div>
                  <span className="eyebrow">运行状态</span>
                  <strong>{dispatchEnabled ? 'SubPilot 接管中' : '仅监控模式'}</strong>
                </div>
                <label className={privacyMode ? 'runtime-switch enabled' : 'runtime-switch'}>
                  <input type="checkbox" role="switch" aria-label="隐私模式" checked={privacyMode} onChange={(event) => handlePrivacyModeChange(event.target.checked)} />
                  <span>{privacyMode ? <EyeOff size={15} /> : <Eye size={15} />} 隐私模式</span>
                  <strong>{privacyMode ? '开启' : '关闭'}</strong>
                </label>
                <label className={dispatchEnabled ? 'runtime-switch enabled' : 'runtime-switch'}>
                  <input type="checkbox" role="switch" aria-label="接管调度" checked={dispatchEnabled} onChange={(event) => void handleDispatchEnabledChange(event.target.checked)} />
                  <span><Power size={15} /> 接管调度</span>
                  <strong>{dispatchEnabled ? '开启' : '关闭'}</strong>
                </label>
                <label className={probeEnabled ? 'runtime-switch enabled' : 'runtime-switch'}>
                  <input type="checkbox" role="switch" aria-label="自动探测" checked={probeEnabled} onChange={(event) => void handleProbeEnabledChange(event.target.checked)} />
                  <span><Activity size={15} /> 自动探测</span>
                  <strong>{probeEnabled ? '开启' : '关闭'}</strong>
                </label>
              </aside>
            </section>

            <section className="control-panel">
              <div className="tabs" role="toolbar" aria-label="分组切换">
                <div className="tabs-scroll" role="tablist" aria-label="group tabs">
                  <section className="group-tab-section dispatching-tab-section" aria-label="调度状态分组">
                    <span className="group-tab-label">实时</span>
                    <div className="group-tab-items">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={dispatchingOnly}
                        className={dispatchingOnly ? 'tab active dispatching-group-tab' : 'tab dispatching-group-tab'}
                        onClick={() => setDispatchingOnly(true)}
                      >
                        <Activity size={13} />
                        <span>正在调度</span>
                        <b>{dashboard?.channels.filter((channel) => channel.isDispatching).length ?? 0}</b>
                      </button>
                    </div>
                  </section>
                  {groupedGroups.map((section) => (
                    <section className="group-tab-section" aria-label={`${section.label} 分组`} key={section.key}>
                      <span className="group-tab-label">{section.label}</span>
                      <div className="group-tab-items">
                        {section.groups.map((group) => (
                          <button
                            type="button"
                            role="tab"
                            aria-selected={!dispatchingOnly && group.id === groupId}
                            className={[
                              !dispatchingOnly && group.id === groupId ? 'tab active' : 'tab',
                              draggingGroupId === group.id ? 'dragging' : '',
                            ].filter(Boolean).join(' ')}
                            key={group.id}
                            draggable
                            onDragStart={(event) => handleGroupDragStart(event, group.id)}
                            onDragOver={(event) => handleGroupDragOver(event, group.id)}
                            onDrop={(event) => handleGroupDrop(event, group.id)}
                            onDragEnd={() => setDraggingGroupId(null)}
                            onClick={() => {
                              setDispatchingOnly(false);
                              setGroupId(group.id);
                              setProvider('all');
                              writeStoredActiveGroup(group.id);
                              setSmartMode(group.mode === 'smart');
                              setMaxAcceptableTtftSec(formatTTFTSeconds(group.maxAcceptableTtftMs));
                              setDispatchStrategy(groupDispatchStrategy(group));
                            }}
                          >
                            <GripVertical size={13} aria-hidden="true" />
                            <span>{group.name}</span>
                          </button>
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              </div>

              <div className="control-bar" role="group" aria-label="筛选条件">
                <div className="dispatch-filter-group">
                  <label className="search-field">
                    <Search size={17} />
                    <input aria-label="搜索账号" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="搜索账号 / 模型 / 分组" />
                  </label>

                  <label className="select-field">
                    <span>平台</span>
                    <select aria-label="平台筛选" value={provider} onChange={(event) => setProvider(event.target.value as Provider | 'all')}>
                      {providerOptions.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}
                    </select>
                  </label>
                </div>

                <div className="dispatch-strategy-group" role="group" aria-label="调度操作">
                  <label className="field ttft-field">
                    <span><Bolt size={13} /> 首字</span>
                    <input
                      className="ttft-threshold-input"
                      aria-label="最长首字"
                      type="text"
                      inputMode="decimal"
                      value={maxAcceptableTtftSec}
                      onChange={(event) => setMaxAcceptableTtftSec(event.target.value)}
                      onBlur={() => void handleMaxAcceptableTtftBlur()}
                    />
                  </label>

                  <div className="view-mode-toggle" role="group" aria-label="卡片视图">
                    {channelViewOptions.map((option) => {
                      const Icon = option.icon;
                      return (
                        <button
                          type="button"
                          key={option.value}
                          className={channelViewMode === option.value ? 'view-mode-button active' : 'view-mode-button'}
                          aria-pressed={channelViewMode === option.value}
                          title={`${option.label}卡片`}
                          onClick={() => handleChannelViewModeChange(option.value)}
                        >
                          <Icon size={15} />
                          <span>{option.label}</span>
                        </button>
                      );
                    })}
                  </div>

                  <label className="channel-sort-field" title="只改变当前页面的卡片顺序，不改变实际调度决策">
                    <ArrowUpDown size={14} aria-hidden="true" />
                    <select
                      aria-label="账号卡片排序"
                      value={channelSortMode}
                      onChange={(event) => handleChannelSortModeChange(event.target.value as ChannelSortMode)}
                    >
                      <option value="recommended">推荐：调度中优先</option>
                      <option value="abnormal_first">异常状态：优先</option>
                      <option value="cost_asc">价格：低到高</option>
                      <option value="cost_desc">价格：高到低</option>
                      <option value="health_desc">健康度：高到低</option>
                      <option value="ttft_asc">TTFT：低到高</option>
                      <option value="score_desc">调度分：高到低</option>
                    </select>
                  </label>

                  <label className="switch-field smart-dispatch-switch">
                    <input type="checkbox" role="switch" aria-label="智能调度" checked={smartMode} onChange={(event) => void handleSmartModeChange(event.target.checked)} />
                    <span><Route size={14} /> 智能调度</span>
                  </label>

                  <select className="bare-select dispatch-strategy-select" aria-label="调度策略" title={dispatchStrategyDescription(dispatchStrategy)} value={dispatchStrategy} onChange={(event) => void handleDispatchStrategyChange(event.target.value as DispatchStrategy)}>
                    <option value="balanced">综合均衡（建议）</option>
                    <option value="stable_first">稳定优先</option>
                    <option value="cost_first">成本优先</option>
                  </select>

                  <label className="switch-field nonstream-monitor-switch" title="关闭后，同步/非流式真实请求不会参与账号健康评分、真实请求柱状图和调度惩罚；自动探测不受影响。">
                    <input
                      type="checkbox"
                      role="switch"
                      aria-label="监测非流式真实请求"
                      checked={groupIncludeNonStreamUsage(activeGroup)}
                      onChange={(event) => void handleIncludeNonStreamUsageChange(event.target.checked)}
                    />
                    <span><Activity size={14} /> 非流式</span>
                  </label>
                </div>

                <div className="dispatch-refresh-group">
                  <button
                    className={isRefreshing ? 'icon-button is-refreshing' : 'icon-button'}
                    type="button"
                    aria-label="立即刷新"
                    title="立即刷新"
                    disabled={isRefreshing}
                    onClick={() => void handleRefresh()}
                  >
                    <RefreshCcw size={15} />
                  </button>
                </div>
              </div>

              <section className="group-context">
                <div>
                  <span className="eyebrow">当前策略</span>
                  <strong>{dispatchingOnly ? '跨分组实时调度账号' : `${activeGroup?.name ?? '全部'} · ${smartMode ? '智能模式' : '强制模式'} · ${dispatchStrategyLabel(groupDispatchStrategy(activeGroup))}`}</strong>
                </div>
                <div>
                  <span className="eyebrow">调度依据</span>
                  <strong>{dispatchingOnly ? '跨分组展示，各分组按自身策略调度' : dispatchStrategyDescription(groupDispatchStrategy(activeGroup))}</strong>
                </div>
                <div>
                  <span className="eyebrow">官方用量</span>
                  <strong>{privacyMode ? PRIVACY_MASK : formatOfficialUSD(activeGroup?.officialUsd24h)}</strong>
                </div>
              </section>
            </section>

            <section className={`channel-grid channel-grid-${effectiveChannelViewMode}`} role="list" aria-label="账号状态卡片">
              {channels.map((channel) => (
                <ChannelCard channel={channel} statisticsWindow={useRolling24h ? '24h' : 'today'} balance={channelBalances[channel.id]} viewMode={effectiveChannelViewMode} refreshLabel={channelRefreshLabel} key={channel.id} privacyMode={privacyMode} onOpen={setSelected} onSaveConfig={(target, config) => void handleSaveChannel(target, config)} onForce={(target) => void handleForceChannel(target)} onSchedulingChange={handleChannelSchedulingChange} />
              ))}
            </section>
          </section>
        )}

        <Suspense fallback={<ModuleLoading title={moduleTitle(activeModule)} />}>
          {activeModule === 'balances' && (
            <section className="module-page" aria-label="渠道余额管理">
              <ChannelBalancePage />
            </section>
          )}

          {activeModule === 'costs' && (
            <section className="module-page" aria-label="成本管理">
              <CostManagementPage />
            </section>
          )}

          {activeModule === 'profit' && (
            <section className="module-page" aria-label="每日收益统计">
              <DailyProfitPage privacyMode={privacyMode} />
            </section>
          )}

          {activeModule === 'dispatchRecords' && (
            <DispatchRecordsPage groups={dashboard?.groups ?? []} initialGroupId={groupId} privacyMode={privacyMode} />
          )}

          {activeModule === 'notifications' && (
            <section className="module-page settings-page notification-settings-page" aria-label="消息推送">
              <NotificationSettingsPage />
            </section>
          )}

          {activeModule === 'system' && (
            <section className="module-page settings-page system-settings-page" aria-label="系统设置">
              <SystemSettingsPage
                dispatchEnabled={dispatchEnabled}
                dispatchOAuthPreference={dispatchOAuthPreference}
                probeEnabled={probeEnabled}
                probeIntervalSeconds={probeIntervalSeconds}
                probeImageIntervalSeconds={probeImageIntervalSeconds}
                probeJitterSeconds={probeJitterSeconds}
                dispatchFailOpen={dispatchFailOpen}
                dispatchSelectTimeoutMs={dispatchSelectTimeoutMs}
                dispatchAutoBypassFailures={dispatchAutoBypassFailures}
                dispatchAutoRecover={dispatchAutoRecover}
                probeMaxConcurrentAccounts={probeMaxConcurrentAccounts}
                probeTimeoutSeconds={probeTimeoutSeconds}
                probeRetryCount={probeRetryCount}
                alertMergeWindowMinutes={alertMergeWindowMinutes}
                dataRetentionDays={dataRetentionDays}
                dashboardUpdatedAt={lastLoadedAtMs}
                dashboardError={dashboardError}
                saveStatus={saveStatus}
                isRefreshing={isRefreshing}
                onDispatchEnabledChange={(nextEnabled) => void handleDispatchEnabledChange(nextEnabled)}
                onProbeEnabledChange={(nextEnabled) => void handleProbeEnabledChange(nextEnabled)}
                onProbeIntervalSecondsChange={setProbeIntervalSeconds}
                onProbeIntervalSecondsBlur={() => void handleProbeIntervalSecondsBlur()}
                onProbeImageIntervalSecondsChange={setProbeImageIntervalSeconds}
                onProbeImageIntervalSecondsBlur={() => void handleProbeImageIntervalSecondsBlur()}
                onProbeJitterSecondsChange={setProbeJitterSeconds}
                onProbeJitterSecondsBlur={() => void handleProbeJitterSecondsBlur()}
                onAdvancedRuntimeChange={(patch, message) => void handleAdvancedRuntimeChange(patch, message)}
                onDataRetentionDaysChange={(nextDays) => void handleDataRetentionDaysChange(nextDays)}
                onRefreshDashboard={() => void handleRefresh()}
              />
            </section>
          )}
        </Suspense>
      </div>

      <DetailDrawer channel={selected} recentDecisions={dashboard?.recentDecisions ?? []} privacyMode={privacyMode} onClose={() => setSelected(null)} />
      <ToastNotice
        message={appToastMessage}
        tone={appToastTone}
        onClose={() => {
          if (dashboardError) setDashboardError('');
          else setSaveStatus('');
        }}
      />
    </main>
  );
}

function dashboardChannelKey(channel: Pick<Channel, 'id' | 'group'>): string {
  return `${channel.group.id}\u0000${channel.id}`;
}

function preserveDashboardTimelines(next: DashboardData, current: DashboardData | null): DashboardData {
  if (!current) return next;
  const previousByKey = new Map(current.channels.map((channel) => [dashboardChannelKey(channel), channel]));
  return {
    ...next,
    channels: next.channels.map((channel) => {
      const previous = previousByKey.get(dashboardChannelKey(channel));
      const hasDetails = previous && (
        previous.timelinesLoading === false ||
        (Array.isArray(previous.timeline) && previous.timeline.length > 0) ||
        (Array.isArray(previous.usageTimeline) && previous.usageTimeline.length > 0) ||
        (Array.isArray(previous.probeTimeline) && previous.probeTimeline.length > 0)
      );
      if (!previous || !hasDetails) return channel;
      return {
        ...channel,
        ttftMs: previous.ttftMs,
        pingMs: previous.pingMs,
        probeLogs: Array.isArray(previous.probeLogs) ? previous.probeLogs : [],
        timeline: Array.isArray(previous.timeline) ? previous.timeline : [],
        usageTimeline: Array.isArray(previous.usageTimeline) ? previous.usageTimeline : [],
        probeTimeline: Array.isArray(previous.probeTimeline) ? previous.probeTimeline : [],
        timelinesLoading: previous.timelinesLoading,
        timelinesError: previous.timelinesError,
      };
    }),
  };
}

function markTimelineGroupsLoading(current: DashboardData | null, groupIds: string[]): DashboardData | null {
  if (!current) return current;
  const targets = new Set(groupIds);
  return {
    ...current,
    channels: current.channels.map((channel) => targets.has(channel.group.id)
      ? { ...channel, timelinesLoading: true, timelinesError: false }
      : channel),
  };
}

function markTimelineGroupsFailed(current: DashboardData | null, groupIds: string[]): DashboardData | null {
  if (!current) return current;
  const targets = new Set(groupIds);
  return {
    ...current,
    channels: current.channels.map((channel) => targets.has(channel.group.id)
      ? { ...channel, timelinesLoading: false, timelinesError: true }
      : channel),
  };
}

function mergeTimelineResponses(current: DashboardData | null, responses: DashboardGroupTimelines[]): DashboardData | null {
  if (!current) return current;
  const details = new Map<string, DashboardGroupTimelines['channels'][number]>();
  const responseGroups = new Set(responses.map((response) => response.groupId));
  responses.forEach((response) => (response.channels ?? []).forEach((detail) => {
    details.set(`${response.groupId}\u0000${detail.accountId}`, detail);
  }));
  return {
    ...current,
    channels: current.channels.map((channel) => {
      const detail = details.get(dashboardChannelKey(channel));
      if (!detail && responseGroups.has(channel.group.id)) {
        return { ...channel, timelinesLoading: false, timelinesError: true };
      }
      return mergeChannelTimeline(channel, detail);
    }),
  };
}

function mergeSelectedTimelineResponses(current: Channel | null, responses: DashboardGroupTimelines[]): Channel | null {
  if (!current) return current;
  const response = responses.find((item) => item.groupId === current.group.id);
  const detail = response?.channels?.find((item) => item.accountId === current.id);
  if (response && !detail) {
    return { ...current, timelinesLoading: false, timelinesError: true };
  }
  return mergeChannelTimeline(current, detail);
}

function mergeChannelTimeline(channel: Channel, detail: DashboardGroupTimelines['channels'][number] | undefined): Channel {
  if (!detail) return channel;
  if (!detail.loaded) {
    return { ...channel, timelinesLoading: false, timelinesError: true };
  }
  return {
    ...channel,
    ttftMs: detail.ttftMs,
    pingMs: detail.pingMs,
    probeLogs: detail.probeLogs ?? [],
    timeline: detail.timeline ?? [],
    usageTimeline: detail.usageTimeline ?? [],
    probeTimeline: detail.probeTimeline ?? [],
    timelinesLoading: false,
    timelinesError: false,
  };
}

function useMobileViewport(): boolean {
  const query = '(max-width: 760px)';
  const [matches, setMatches] = useState(() => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(query).matches);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia(query);
    const update = () => setMatches(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  return matches;
}

function refreshCountdownLabel(lastLoadedAtMs: number | null, nowMs: number): string {
  if (!lastLoadedAtMs) return '等待刷新';
  const elapsedSeconds = Math.max(0, Math.floor((nowMs - lastLoadedAtMs) / 1000));
  if (elapsedSeconds < 2) return '刚刚刷新';
  if (elapsedSeconds >= DASHBOARD_REFRESH_SECONDS) return '即将刷新';
  return `${DASHBOARD_REFRESH_SECONDS - elapsedSeconds}s 后刷新`;
}

function buildChannelBalanceMap(
  balanceAccounts: ChannelBalanceAccount[],
  costOverview: CostManagementOverview | null,
): Record<string, ChannelCardBalance> {
  const balanceByID = new Map(balanceAccounts.map((account) => [account.id, account]));
  const result: Record<string, ChannelCardBalance> = {};
  for (const upstream of costOverview?.upstreams ?? []) {
    const balance = balanceByID.get(upstream.balance_account_id);
    if (!balance || !balance.enabled) continue;
    for (const account of upstream.accounts) {
      if (!account.account_id) continue;
      result[account.account_id] = {
        name: balance.name,
        convertedBalance: balance.converted_balance,
        unit: balance.unit,
        lowBalance: balance.low_balance,
      };
    }
  }
  return result;
}

function moduleTitle(module: AdminModule): string {
  switch (module) {
    case 'dispatchRecords':
      return '调度记录';
    case 'balances':
      return '渠道余额管理';
    case 'costs':
      return '成本管理';
    case 'profit':
      return '每日收益统计';
    case 'notifications':
      return '消息推送';
    case 'system':
      return '系统设置';
    default:
      return '智能调度';
  }
}

function ModuleLoading({ title }: { title: string }) {
  return (
    <section className="module-page module-loading" aria-label={title}>
      <div className="module-loading-card">
        <span className="eyebrow">加载中</span>
        <strong>{title}</strong>
        <p>正在按需加载当前页面组件。</p>
      </div>
    </section>
  );
}

function formatCompositeCost(value?: number): string {
  if (!value || value <= 0) return '暂无用量';
  return `${value.toFixed(4)} 元/美元`;
}

function formatOfficialUSD(value?: number): string {
  if (!value || value <= 0) return '24h 官方美元 0.0000';
  return `24h 官方美元 ${value.toFixed(4)}`;
}

function groupMaxAcceptableTTFTMS(group: { maxAcceptableTtftMs?: number }): number {
  return group.maxAcceptableTtftMs && group.maxAcceptableTtftMs > 0 ? group.maxAcceptableTtftMs : DEFAULT_MAX_ACCEPTABLE_TTFT_MS;
}

function groupDispatchStrategy(group?: { dispatchStrategy?: DispatchStrategy }): DispatchStrategy {
  if (group?.dispatchStrategy === 'cost_first' || group?.dispatchStrategy === 'balanced') return group.dispatchStrategy;
  return DEFAULT_DISPATCH_STRATEGY;
}

function groupIncludeNonStreamUsage(group?: { includeNonStreamUsage?: boolean }): boolean {
  return group?.includeNonStreamUsage ?? true;
}

function groupForcedFallback(group?: Pick<GroupSummary, 'mode' | 'forcedChannelId'>): 'smart' | 'reject' {
  return group?.mode === 'forced' && group.forcedChannelId ? 'reject' : 'smart';
}

function dispatchStrategyLabel(strategy: DispatchStrategy): string {
  if (strategy === 'cost_first') return '成本优先';
  if (strategy === 'balanced') return '综合均衡';
  return '稳定优先';
}

function dispatchStrategyDescription(strategy: DispatchStrategy): string {
  if (strategy === 'cost_first') return '达到健康门槛后，优先选择低成本账号';
  if (strategy === 'balanced') return '5 分钟健康门槛 + 30 分钟/1 小时稳定性 + TTFT/成本';
  return '优先近期稳定性与低延迟，成本仅作次要因素';
}

function statusTone(message: string): ToastTone {
  if (!message) return 'info';
  if (message.includes('失败') || message.includes('无效') || message.includes('不可用')) return 'error';
  if (message.includes('正在')) return 'info';
  return 'success';
}

function normalizeProbeIntervalSeconds(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 60;
  return Math.min(86400, Math.max(30, Math.round(value)));
}

function normalizeProbeImageIntervalSeconds(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 1200;
  return Math.min(86400, Math.max(60, Math.round(value)));
}

function formatIntervalMinutes(seconds: number): string {
  const minutes = seconds / 60;
  return Number.isInteger(minutes) ? `${minutes} 分钟` : `${seconds} 秒`;
}

function normalizeProbeJitterSeconds(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.min(3600, Math.round(value));
}

function normalizeDataRetentionDays(value: number): number {
  return [1, 3, 7].includes(value) ? value : 7;
}

function formatTTFTSeconds(valueMS: number): string {
  return (groupMaxAcceptableTTFTMS({ maxAcceptableTtftMs: valueMS }) / 1000).toFixed(2);
}

function ttftSecondsInputToMS(value: string, fallbackMS: number): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return groupMaxAcceptableTTFTMS({ maxAcceptableTtftMs: fallbackMS });
  }
  return Math.round(parsed * 1000);
}

function readStoredChannelViewMode(): ChannelViewMode {
  if (typeof window === 'undefined') return 'large';
  const stored = window.localStorage.getItem(CHANNEL_VIEW_STORAGE_KEY);
  return stored === 'medium' || stored === 'compact' ? stored : 'large';
}

function isAdminModule(value: string | null): value is AdminModule {
  return value === 'dispatch' ||
    value === 'dispatchRecords' ||
    value === 'balances' ||
    value === 'costs' ||
    value === 'profit' ||
    value === 'notifications' ||
    value === 'system';
}

function readActiveModuleFromLocation(): AdminModule | null {
  if (typeof window === 'undefined') return null;
  const value = new URLSearchParams(window.location.search).get('module');
  if (value === 'settings') return 'notifications';
  return isAdminModule(value) ? value : null;
}

function readStoredActiveModule(): AdminModule {
  if (typeof window === 'undefined') return 'dispatch';
  const fromURL = readActiveModuleFromLocation();
  if (fromURL) return fromURL;
  const stored = window.localStorage.getItem(ACTIVE_MODULE_STORAGE_KEY);
  if (stored === 'settings') return 'notifications';
  return isAdminModule(stored) ? stored : 'dispatch';
}

function syncActiveModuleLocation(module: AdminModule) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(ACTIVE_MODULE_STORAGE_KEY, module);
  const url = new URL(window.location.href);
  if (module === 'dispatch') {
    url.searchParams.delete('module');
  } else {
    url.searchParams.set('module', module);
  }
  const next = `${url.pathname}${url.search}${url.hash}`;
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (next !== current) window.history.replaceState(null, '', next);
}

function writeStoredChannelViewMode(mode: ChannelViewMode) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(CHANNEL_VIEW_STORAGE_KEY, mode);
}

function readStoredChannelSortMode(): ChannelSortMode {
  if (typeof window === 'undefined') return 'recommended';
  const stored = window.localStorage.getItem(CHANNEL_SORT_STORAGE_KEY);
  return stored === 'abnormal_first' || stored === 'cost_asc' || stored === 'cost_desc' || stored === 'health_desc' || stored === 'ttft_asc' || stored === 'score_desc'
    ? stored
    : 'recommended';
}

function writeStoredChannelSortMode(mode: ChannelSortMode) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(CHANNEL_SORT_STORAGE_KEY, mode);
}

function readStoredActiveGroup(): string {
  if (typeof window === 'undefined') return 'plus';
  return window.localStorage.getItem(ACTIVE_GROUP_STORAGE_KEY) || 'plus';
}

function writeStoredActiveGroup(groupId: string) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(ACTIVE_GROUP_STORAGE_KEY, groupId);
}

function readStoredGroupOrder(): string[] {
  if (typeof window === 'undefined') return [];
  const raw = window.localStorage.getItem(GROUP_ORDER_STORAGE_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string') : [];
  } catch {
    return [];
  }
}

function writeStoredGroupOrder(order: string[]) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(GROUP_ORDER_STORAGE_KEY, JSON.stringify(order));
}

function orderGroups(groups: GroupSummary[], storedOrder: string[]): GroupSummary[] {
  if (storedOrder.length === 0) return groups;
  const orderIndex = new Map(storedOrder.map((id, index) => [id, index]));
  return [...groups].sort((left, right) => {
    const leftIndex = orderIndex.get(left.id) ?? Number.MAX_SAFE_INTEGER;
    const rightIndex = orderIndex.get(right.id) ?? Number.MAX_SAFE_INTEGER;
    if (leftIndex !== rightIndex) return leftIndex - rightIndex;
    return groups.indexOf(left) - groups.indexOf(right);
  });
}

function groupGroupsByProvider(groups: GroupSummary[], channels: Channel[]): Array<{
  key: Provider | 'mixed';
  label: string;
  groups: GroupSummary[];
}> {
  const providerLabels: Record<Provider | 'mixed', string> = {
    openai: 'OpenAI',
    anthropic: 'Anthropic',
    gemini: 'Gemini',
    openrouter: 'OpenRouter',
    grok: 'Grok',
    mixed: '混合',
  };
  const groupProviders = new Map<string, Set<Provider>>();
  channels.forEach((channel) => {
    const providers = groupProviders.get(channel.group.id) ?? new Set<Provider>();
    providers.add(channel.provider);
    groupProviders.set(channel.group.id, providers);
  });
  const sections = new Map<Provider | 'mixed', GroupSummary[]>();
  groups.forEach((group) => {
    const providers = [...(groupProviders.get(group.id) ?? [])];
    const key: Provider | 'mixed' = providers.length === 1 ? providers[0] : 'mixed';
    sections.set(key, [...(sections.get(key) ?? []), group]);
  });
  const sectionOrder: Array<Provider | 'mixed'> = ['openai', 'anthropic', 'gemini', 'openrouter', 'grok', 'mixed'];
  return sectionOrder
    .filter((key) => (sections.get(key)?.length ?? 0) > 0)
    .map((key) => ({ key, label: providerLabels[key], groups: sections.get(key) ?? [] }));
}

function moveGroupInOrder(currentOrder: string[], draggedId: string, targetId: string): string[] {
  const nextOrder = currentOrder.filter((id) => id !== draggedId);
  const targetIndex = nextOrder.indexOf(targetId);
  if (targetIndex < 0) return currentOrder;
  nextOrder.splice(targetIndex, 0, draggedId);
  return nextOrder;
}

function groupSectionKey(
  sections: Array<{ key: Provider | 'mixed'; groups: GroupSummary[] }>,
  groupId: string,
): Provider | 'mixed' | undefined {
  return sections.find((section) => section.groups.some((group) => group.id === groupId))?.key;
}

export default App;
